import React, {useMemo} from 'react';
import SinisterTable from '../../views/sinister/sinister-table';
import useStore from '../../hooks/use-store';
import useUsers from '../../hooks/use-users';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {observer} from 'mobx-react';
import {getSnapshot} from 'mobx-state-tree';

/**
 * PERFORMANCE: o criador de cada sinistro sai de um índice por id, montado uma vez por lista de
 * usuários, e não de uma busca linear na lista a cada sinistro (sinistros × usuários por render).
 *
 * @param {Array<{id: number}>} users usuários visíveis ao perfil logado
 * @return {Map<number, object>} usuário por id
 */
export const indexUsersById = (users) => new Map(users.map((user) => [user.id, user]));

/**
 * PERFORMANCE: uma data só é formatada quando a linha é lida (tabela mostra poucas linhas por
 * página) e uma vez só. O valor fica enumerável, então cópia por spread e JSON levam o texto.
 *
 * @param {object} row linha que recebe a propriedade
 * @param {string} name propriedade formatada
 * @param {Function} format devolve o texto, chamada na primeira leitura
 */
const defineLazy = (row, name, format) => {
    let value;
    let done = false;
    Object.defineProperty(row, name, {
        enumerable: true,
        configurable: true,
        get: () => {
            if (!done) {
                value = format();
                done = true;
            }
            return value;
        },
    });
};

export const formatSinisters = (arr, creatorsById = new Map(), timezone) => {
    return arr.map((item) => {
        const creator = creatorsById.get(item.createdByUserId);
        const row = {
            ...item,
            creatorName: creator ? creator.name : '',
            creatorEmail: creator ? creator.email : '',
        };
        defineLazy(row, 'createdAtFormatted', () => formatDateTimeColumn(item.createdAt, timezone));
        defineLazy(row, 'updatedAtFormatted', () => formatDateTimeColumn(item.updatedAt, timezone));
        return row;
    });
};

/**
 * PERFORMANCE: a tela recalcula a largura das colunas sempre que a identidade de `list` muda; a
 * lista é memorizada para não ser recriada a cada render.
 *
 * @param {object} props sem uso
 * @return {JSX.Element} tabela de sinistros
 */
const SinisterContainer = (props) => {
    const {store} = useStore();
    const {sinisterStore} = store;
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();

    const incidents = getSnapshot(sinisterStore.incidents);
    const users = getSnapshot(userStore.users);
    const list = useMemo(
        () => formatSinisters(incidents, indexUsersById(users), timezone),
        [incidents, users, timezone],
    );

    return <SinisterTable list={list}/>;
};

export default observer(SinisterContainer);
